import { UIColorTokens } from "@/core/constants/uiColors";
import { useTranslation, useUIColors } from "@/core/hooks";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import React from "react";
import { StyleSheet, TouchableOpacity } from "react-native";
import { Typography } from "./Typography";

interface LanguageToggleProps {
  onLanguageChange?: ((language: "en" | "ar") => void) | undefined;
  style?: any;
  textStyle?: any;
  color?: keyof UIColorTokens;
}

export const LanguageToggle: React.FC<LanguageToggleProps> = ({
  onLanguageChange,
  style,
  textStyle,
  color = "fontPrimary",
}) => {
  const { currentLanguage, toggleLanguage } = useTranslation();
  const { isRTL } = useI18nContext();
  const colors = useUIColors();

  const handleToggle = async () => {
    try {
      logger.info("LanguageToggle", "Toggling language", { currentLanguage });
      const newLanguage = await toggleLanguage();
      logger.info("LanguageToggle", "Language toggled successfully", {
        newLanguage,
      });
      onLanguageChange?.(newLanguage);
    } catch (error) {
      logger.error("LanguageToggle", "Failed to toggle language:", error);
    }
  };

  // Show the opposite language as the button text
  // If currentLanguage is 'en', show Arabic text (meaning "click to switch to Arabic")
  // If currentLanguage is 'ar', show English text (meaning "click to switch to English")
  const displayText = currentLanguage === "en" ? "العربية" : "English";

  return (
    <TouchableOpacity
      style={[
        styles.container,
        { alignItems: isRTL ? "flex-start" : "flex-end" },
        style,
      ]}
      onPress={handleToggle}
      activeOpacity={0.7}
    >
      <Typography
        variant="body"
        numberOfLines={1}
        style={[
          styles.text,
          {
            color: colors[color],
            textAlign: isRTL ? "right" : "left",
          },
          textStyle,
        ]}
      >
        {displayText}
      </Typography>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: {
    minWidth: 50,
    minHeight: 40,
    justifyContent: "center",
  },
  text: {
    fontWeight: "500",
    lineHeight: 20,
    includeFontPadding: false,
    textAlignVertical: "center",
  },
});